import { useState, useEffect, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import "@/App.css";
import axios from "axios";
import { 
  Wheat, Plus, Trash2, Edit2, Download, LayoutDashboard, Package, 
  FileText, TrendingUp, TrendingDown, AlertTriangle, CheckCircle2,
  Users, Archive, ArrowUpCircle, ArrowDownCircle, Calendar, LogOut, Settings, UserCircle, Building2, Printer, Truck, Factory, FolderTree, Phone, User, Globe, ClipboardList, Database, Search, ArrowUp, ArrowDown, ArrowUpDown, X, BarChart3, Receipt, History
} from "lucide-react";
import UserManagement from "./UserManagement";
import BackupSettings from "./BackupSettings";
import WarehouseManagement from "./WarehouseManagement";
import PrintStock from "./PrintStock";
import PrintChallan from "./PrintChallan";
import SavedChallans from "./SavedChallans";
import AuditLogs from "./AuditLogs";
import LocalBackupRestore from "./LocalBackupRestore";
import InventoryReports from "./InventoryReports";
import InventoryStatements from "./InventoryStatements";
import ArchiveManager from "./ArchiveManager";
import { TransactionEditSheet, TransactionHistoryDialog, EditedBadge } from "./TransactionEdit";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
  SheetFooter,
} from "@/components/ui/sheet";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import * as XLSX from "xlsx";
import { useLanguage } from "../LanguageContext";
import TablePagination from "./ui/TablePagination";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Universal Search Input Component
const SearchInput = ({ value, onChange, placeholder = "Search...", className = "" }) => (
  <div className={`relative ${className}`}>
    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-stone-400" />
    <Input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="pl-10 pr-8"
      data-testid="search-input"
    />
    {value && (
      <button
        onClick={() => onChange('')}
        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-stone-400 hover:text-stone-600"
      >
        <X className="w-4 h-4" />
      </button>
    )}
  </div>
);

// Sortable Header Component
const SortableHeader = ({ column, label, sortConfig, onSort, className = "" }) => {
  const isSorted = sortConfig?.key === column;
  const direction = sortConfig?.direction;

  const handleClick = () => {
    let newDirection = 'asc';
    if (isSorted) {
      if (direction === 'asc') newDirection = 'desc';
      else if (direction === 'desc') newDirection = null;
    }
    onSort(newDirection ? { key: column, direction: newDirection } : null);
  };

  return (
    <TableHead 
      className={`cursor-pointer hover:bg-stone-50 select-none ${className}`}
      onClick={handleClick}
    >
      <div className="flex items-center gap-1">
        <span>{label}</span>
        {isSorted ? (
          direction === 'asc' ? <ArrowUp className="w-4 h-4 text-lime-600" /> : <ArrowDown className="w-4 h-4 text-lime-600" />
        ) : (
          <ArrowUpDown className="w-4 h-4 text-stone-300" />
        )}
      </div>
    </TableHead>
  );
};

// Utility functions for sorting and filtering
const sortData = (data, sortConfig) => {
  if (!sortConfig || !sortConfig.key) return data;
  return [...data].sort((a, b) => {
    let aVal = a[sortConfig.key];
    let bVal = b[sortConfig.key];
    if (aVal == null) return sortConfig.direction === 'asc' ? 1 : -1;
    if (bVal == null) return sortConfig.direction === 'asc' ? -1 : 1;
    if (sortConfig.key.includes('date') || sortConfig.key.includes('Date')) {
      aVal = new Date(aVal).getTime();
      bVal = new Date(bVal).getTime();
    }
    if (typeof aVal === 'string') aVal = aVal.toLowerCase();
    if (typeof bVal === 'string') bVal = bVal.toLowerCase();
    if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
    return 0;
  });
};

const filterData = (data, searchTerm, searchFields) => {
  if (!searchTerm?.trim()) return data;
  const term = searchTerm.toLowerCase().trim();
  return data.filter(item => 
    searchFields.some(field => {
      const value = item[field];
      return value != null && String(value).toLowerCase().includes(term);
    })
  );
};

function Dashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useLanguage();
  const [currentUser, setCurrentUser] = useState(null);
  const [currentWarehouse, setCurrentWarehouse] = useState(null);
  const [products, setProducts] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [warehouses, setWarehouses] = useState(['Main Warehouse']);
  const [categories, setCategories] = useState([]);
  const [activeTab, setActiveTab] = useState("dashboard");
  const [masterTab, setMasterTab] = useState("products");
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [showChallanModal, setShowChallanModal] = useState(false);
  const [selectedChallanTransactions, setSelectedChallanTransactions] = useState([]);
  const [selectedTransactionIds, setSelectedTransactionIds] = useState([]);
  const [showOpeningStockModal, setShowOpeningStockModal] = useState(false);
  const [openingStockForm, setOpeningStockForm] = useState({ product_id: '', quantity: 0 });
  
  // Sheet states
  const [isProductSheetOpen, setIsProductSheetOpen] = useState(false);
  const [isVendorSheetOpen, setIsVendorSheetOpen] = useState(false);
  const [isTransactionSheetOpen, setIsTransactionSheetOpen] = useState(false);
  const [isCategorySheetOpen, setIsCategorySheetOpen] = useState(false);
  const [isManufacturingSheetOpen, setIsManufacturingSheetOpen] = useState(false);
  
  // Edit states
  const [editingProduct, setEditingProduct] = useState(null);
  const [editingVendor, setEditingVendor] = useState(null);
  const [editingCategory, setEditingCategory] = useState(null);
  const [editingBatch, setEditingBatch] = useState(null);  // For manufacturing batch edit
  
  // Saved Challans state
  const [savedChallans, setSavedChallans] = useState([]);
  const [showSavedChallansModal, setShowSavedChallansModal] = useState(false);
  const [challanFilters, setChallanFilters] = useState({
    start_date: "",
    end_date: "",
    party_name: "",
    challan_number: ""
  });
  
  // Transaction Edit states
  const [editingTransaction, setEditingTransaction] = useState(null);
  const [showTransactionEditSheet, setShowTransactionEditSheet] = useState(false);
  const [showTransactionHistory, setShowTransactionHistory] = useState(false);
  const [selectedTransactionForHistory, setSelectedTransactionForHistory] = useState(null);
  const [recentlyUpdatedTransactionId, setRecentlyUpdatedTransactionId] = useState(null);
  
  // Archive states
  const [selectedArchiveItems, setSelectedArchiveItems] = useState([]);
  const [selectedProductsForArchive, setSelectedProductsForArchive] = useState([]);
  const [selectedVendorsForArchive, setSelectedVendorsForArchive] = useState([]);
  const [selectedCategoriesForArchive, setSelectedCategoriesForArchive] = useState([]);
  const [archiveLoading, setArchiveLoading] = useState(false);
  
  // Pagination states (default 50 rows per page)
  const [transactionPage, setTransactionPage] = useState(1);
  const [transactionPageSize, setTransactionPageSize] = useState(50);
  const [productPage, setProductPage] = useState(1);
  const [productPageSize, setProductPageSize] = useState(50);
  const [vendorPage, setVendorPage] = useState(1);
  const [vendorPageSize, setVendorPageSize] = useState(50);
  const [inventoryPage, setInventoryPage] = useState(1);
  const [inventoryPageSize, setInventoryPageSize] = useState(50);
  
  // Archive handler function
  const handleArchiveItems = async (itemIds, itemType) => {
    if (itemIds.length === 0) {
      toast.error("Please select items to archive");
      return;
    }
    
    if (!window.confirm(`Archive ${itemIds.length} ${itemType}(s)? They will be moved to the Archive tab.`)) {
      return;
    }
    
    setArchiveLoading(true);
    try {
      await axios.post(`${API}/archive/items?item_type=${itemType}`, itemIds, { withCredentials: true });
      toast.success(`${itemIds.length} ${itemType}(s) archived successfully`);
      
      // Clear selections and refresh data
      if (itemType === 'product') {
        setSelectedProductsForArchive([]);
        fetchProducts();
      } else if (itemType === 'vendor') {
        setSelectedVendorsForArchive([]);
        fetchVendors();
      } else if (itemType === 'category') {
        setSelectedCategoriesForArchive([]);
        fetchCategories();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to archive ${itemType}(s)`);
    } finally {
      setArchiveLoading(false);
    }
  };
  
  // Form data
  const [productForm, setProductForm] = useState({ product_name: "", category: "" });
  const [vendorForm, setVendorForm] = useState({ vendor_name: "", contact: "" });
  const [transactionForm, setTransactionForm] = useState({
    product_id: "",
    vendor_id: "",
    transaction_type: "in",
    quantity: 0,
    notes: "",
    driver_name: "",
    driver_contact: "",
    vehicle_number: ""
  });
  const [categoryForm, setCategoryForm] = useState({ name: "", description: "" });
  
  // Manufacturing state
  const [manufacturingBatches, setManufacturingBatches] = useState([]);
  const [manufacturingForm, setManufacturingForm] = useState({
    inputs: [],
    output_product_id: "",
    output_quantity: 0,
    process_loss: 0,
    process_loss_notes: ""
  });
  const [inputItems, setInputItems] = useState([{ product_id: "", quantity: 0 }]);
  
  // Report filters
  const [reportFilters, setReportFilters] = useState({
    product_id: "",
    vendor_id: "",
    start_date: "",
    end_date: "",
    sort_by: "date"
  });

  // Universal Search States
  const [productSearch, setProductSearch] = useState('');
  const [vendorSearch, setVendorSearch] = useState('');
  const [transactionSearch, setTransactionSearch] = useState('');
  const [inventorySearch, setInventorySearch] = useState('');
  const [manufacturingSearch, setManufacturingSearch] = useState('');
  const [categorySearch, setCategorySearch] = useState('');

  // Universal Sort States
  const [productSort, setProductSort] = useState(null);
  const [vendorSort, setVendorSort] = useState(null);
  const [transactionSort, setTransactionSort] = useState({ key: 'transaction_date', direction: 'desc' });
  const [inventorySort, setInventorySort] = useState(null);
  const [manufacturingSort, setManufacturingSort] = useState({ key: 'batch_date', direction: 'desc' });
  const [categorySort, setCategorySort] = useState(null);

  // Filtered and Sorted Data using useMemo for performance
  const filteredProducts = useMemo(() => {
    let data = filterData(products, productSearch, ['product_name', 'category']);
    return sortData(data, productSort);
  }, [products, productSearch, productSort]);

  const filteredVendors = useMemo(() => {
    let data = filterData(vendors, vendorSearch, ['vendor_name', 'contact']);
    return sortData(data, vendorSort);
  }, [vendors, vendorSearch, vendorSort]);

  const filteredTransactions = useMemo(() => {
    let data = filterData(transactions, transactionSearch, ['product_name', 'vendor_name', 'notes', 'driver_name', 'vehicle_number']);
    return sortData(data, transactionSort);
  }, [transactions, transactionSearch, transactionSort]);

  const filteredInventory = useMemo(() => {
    let data = filterData(inventory, inventorySearch, ['product_name', 'category', 'vendor']);
    return sortData(data, inventorySort);
  }, [inventory, inventorySearch, inventorySort]);

  const filteredManufacturing = useMemo(() => {
    let data = filterData(manufacturingBatches, manufacturingSearch, ['batch_number', 'output_product_name']);
    return sortData(data, manufacturingSort);
  }, [manufacturingBatches, manufacturingSearch, manufacturingSort]);

  const filteredCategories = useMemo(() => {
    let data = filterData(categories, categorySearch, ['name', 'description']);
    return sortData(data, categorySort);
  }, [categories, categorySearch, categorySort]);

  // Paginated data calculations
  const paginatedTransactions = useMemo(() => {
    const startIndex = (transactionPage - 1) * transactionPageSize;
    return filteredTransactions.slice(startIndex, startIndex + transactionPageSize);
  }, [filteredTransactions, transactionPage, transactionPageSize]);

  const totalTransactionPages = useMemo(() => 
    Math.ceil(filteredTransactions.length / transactionPageSize), 
    [filteredTransactions.length, transactionPageSize]
  );

  const paginatedProducts = useMemo(() => {
    const startIndex = (productPage - 1) * productPageSize;
    return filteredProducts.slice(startIndex, startIndex + productPageSize);
  }, [filteredProducts, productPage, productPageSize]);

  const totalProductPages = useMemo(() => 
    Math.ceil(filteredProducts.length / productPageSize), 
    [filteredProducts.length, productPageSize]
  );

  const paginatedVendors = useMemo(() => {
    const startIndex = (vendorPage - 1) * vendorPageSize;
    return filteredVendors.slice(startIndex, startIndex + vendorPageSize);
  }, [filteredVendors, vendorPage, vendorPageSize]);

  const totalVendorPages = useMemo(() => 
    Math.ceil(filteredVendors.length / vendorPageSize), 
    [filteredVendors.length, vendorPageSize]
  );

  const paginatedInventory = useMemo(() => {
    const startIndex = (inventoryPage - 1) * inventoryPageSize;
    return filteredInventory.slice(startIndex, startIndex + inventoryPageSize);
  }, [filteredInventory, inventoryPage, inventoryPageSize]);

  const totalInventoryPages = useMemo(() => 
    Math.ceil(filteredInventory.length / inventoryPageSize), 
    [filteredInventory.length, inventoryPageSize]
  );

  // Reset to page 1 when filters change
  useEffect(() => { setTransactionPage(1); }, [transactionSearch, transactionSort]);
  useEffect(() => { setProductPage(1); }, [productSearch, productSort]);
  useEffect(() => { setVendorPage(1); }, [vendorSearch, vendorSort]);
  useEffect(() => { setInventoryPage(1); }, [inventorySearch, inventorySort]);

  // Fetch current user
  useEffect(() => {
    const fetchUser = async () => {
      try {
        const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
        setCurrentUser(response.data);
        setCurrentWarehouse(response.data.current_warehouse || location.state?.warehouse || 'Main Warehouse');
      } catch (error) {
        navigate('/login');
      }
    };
    fetchUser();
  }, [navigate, location.state]);

  // Fetch data
  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/products`, { withCredentials: true });
      setProducts(response.data);
    } catch (error) {
      console.error("Error fetching products:", error);
    }
  };

  const fetchVendors = async () => {
    try {
      const response = await axios.get(`${API}/vendors`, { withCredentials: true });
      setVendors(response.data);
    } catch (error) {
      console.error("Error fetching vendors:", error);
    }
  };

  const fetchTransactions = async () => {
    try {
      const params = new URLSearchParams();
      if (reportFilters.product_id) params.append("product_id", reportFilters.product_id);
      if (reportFilters.vendor_id) params.append("vendor_id", reportFilters.vendor_id);
      if (reportFilters.start_date) params.append("start_date", reportFilters.start_date);
      if (reportFilters.end_date) params.append("end_date", reportFilters.end_date);
      params.append("sort_by", reportFilters.sort_by);
      
      const response = await axios.get(`${API}/transactions?${params}`, { withCredentials: true });
      setTransactions(response.data);
    } catch (error) {
      console.error("Error fetching transactions:", error);
    }
  };

  const fetchInventory = async () => {
    try {
      const response = await axios.get(`${API}/inventory`, { withCredentials: true });
      setInventory(response.data);
    } catch (error) {
      console.error("Error fetching inventory:", error);
    }
  };

  const fetchWarehouses = async () => {
    try {
      const response = await axios.get(`${API}/warehouses`, { withCredentials: true });
      setWarehouses(response.data.length ? response.data : ['Main Warehouse']);
    } catch (error) {
      console.error("Error fetching warehouses:", error);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`, { withCredentials: true });
      setCategories(response.data);
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  const fetchManufacturingBatches = async () => {
    try {
      const response = await axios.get(`${API}/manufacturing/batches`, { withCredentials: true });
      setManufacturingBatches(response.data);
    } catch (error) {
      console.error("Error fetching manufacturing batches:", error);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      navigate('/login');
    } catch (error) {
      navigate('/login');
    }
  };

  // Role-based permission helper
  const hasPermission = (requiredRole) => {
    if (!currentUser) return false;
    const hierarchy = { viewer: 0, manager: 1, admin: 2 };
    return hierarchy[currentUser.role] >= hierarchy[requiredRole];
  };

  useEffect(() => {
    fetchProducts();
    fetchVendors();
    fetchTransactions();
    fetchInventory();
    fetchWarehouses();
    fetchCategories();
    fetchManufacturingBatches();
  }, []);

  useEffect(() => {
    fetchTransactions();
  }, [reportFilters]);

  // Product handlers
  const handleProductSubmit = async (e) => {
    e.preventDefault();
    if (!productForm.product_name || !productForm.category) {
      toast.error("Please fill all required fields");
      return;
    }

    try {
      if (editingProduct) {
        await axios.put(`${API}/products/${editingProduct.id}`, productForm, { withCredentials: true });
        toast.success("Product updated successfully");
      } else {
        await axios.post(`${API}/products`, productForm, { withCredentials: true });
        toast.success("Product added successfully");
      }
      setProductForm({ product_name: "", category: "" });
      setEditingProduct(null);
      setIsProductSheetOpen(false);
      fetchProducts();
      fetchInventory();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save product");
    }
  };

  const handleDeleteProduct = async (id) => {
    if (!window.confirm("Are you sure you want to delete this product?")) return;
    try {
      await axios.delete(`${API}/products/${id}`);
      toast.success("Product deleted successfully");
      fetchProducts();
      fetchInventory();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete product");
    }
  };

  // Vendor handlers
  const handleVendorSubmit = async (e) => {
    e.preventDefault();
    if (!vendorForm.vendor_name) {
      toast.error("Please enter vendor name");
      return;
    }

    try {
      if (editingVendor) {
        await axios.put(`${API}/vendors/${editingVendor.id}`, vendorForm);
        toast.success("Vendor updated successfully");
      } else {
        await axios.post(`${API}/vendors`, vendorForm);
        toast.success("Vendor added successfully");
      }
      setVendorForm({ vendor_name: "", contact: "" });
      setEditingVendor(null);
      setIsVendorSheetOpen(false);
      fetchVendors();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save vendor");
    }
  };

  const handleDeleteVendor = async (id) => {
    if (!window.confirm("Are you sure you want to delete this vendor?")) return;
    try {
      await axios.delete(`${API}/vendors/${id}`);
      toast.success("Vendor deleted successfully");
      fetchVendors();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete vendor");
    }
  };

  // Opening Stock handler
  const handleOpeningStockSubmit = async (e) => {
    e.preventDefault();
    if (!openingStockForm.product_id || openingStockForm.quantity < 0) {
      toast.error("Please select a product and enter valid quantity");
      return;
    }

    try {
      await axios.post(`${API}/opening-stock`, {
        product_id: openingStockForm.product_id,
        warehouse: currentWarehouse,
        quantity: openingStockForm.quantity
      }, { withCredentials: true });
      toast.success("Opening stock saved successfully");
      setOpeningStockForm({ product_id: '', quantity: 0 });
      setShowOpeningStockModal(false);
      fetchInventory();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save opening stock");
    }
  };

  // Transaction handlers
  const handleTransactionSubmit = async (e) => {
    e.preventDefault();
    if (!transactionForm.product_id || !transactionForm.vendor_id || transactionForm.quantity <= 0) {
      toast.error("Please fill all required fields");
      return;
    }
    
    // Validate logistics fields
    if (!transactionForm.driver_name || !transactionForm.driver_contact || !transactionForm.vehicle_number) {
      toast.error(t('transactions.driverName') + ", " + t('transactions.driverContact') + ", " + t('transactions.vehicleNumber') + " - Required");
      return;
    }

    try {
      await axios.post(`${API}/transactions`, transactionForm, { withCredentials: true });
      toast.success("Transaction recorded successfully");
      setTransactionForm({
        product_id: "",
        vendor_id: "",
        transaction_type: "in",
        quantity: 0,
        notes: "",
        driver_name: "",
        driver_contact: "",
        vehicle_number: ""
      });
      setIsTransactionSheetOpen(false);
      fetchTransactions();
      fetchInventory();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to record transaction");
    }
  };

  // Category handlers
  const handleCategorySubmit = async (e) => {
    e.preventDefault();
    if (!categoryForm.name) {
      toast.error("Category name is required");
      return;
    }

    try {
      if (editingCategory) {
        await axios.put(`${API}/categories/${editingCategory.id}`, categoryForm, { withCredentials: true });
        toast.success("Category updated successfully");
      } else {
        await axios.post(`${API}/categories`, categoryForm, { withCredentials: true });
        toast.success("Category created successfully");
      }
      setCategoryForm({ name: "", description: "" });
      setEditingCategory(null);
      setIsCategorySheetOpen(false);
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save category");
    }
  };

  const handleDeleteCategory = async (id) => {
    if (!window.confirm("Are you sure you want to delete this category?")) return;
    try {
      await axios.delete(`${API}/categories/${id}`, { withCredentials: true });
      toast.success("Category deleted successfully");
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete category");
    }
  };

  // Manufacturing handlers
  const handleManufacturingSubmit = async (e) => {
    e.preventDefault();
    
    // Validate inputs
    const validInputs = inputItems.filter(item => item.product_id && item.quantity > 0);
    if (validInputs.length === 0) {
      toast.error(t('manufacturing.selectInputs') + " - Required");
      return;
    }
    
    if (!manufacturingForm.output_product_id || manufacturingForm.output_quantity <= 0) {
      toast.error(t('manufacturing.selectOutput') + " and " + t('manufacturing.outputQuantity') + " - Required");
      return;
    }

    try {
      const batchData = {
        inputs: validInputs,
        output_product_id: manufacturingForm.output_product_id,
        output_quantity: manufacturingForm.output_quantity,
        process_loss: manufacturingForm.process_loss || 0,
        process_loss_notes: manufacturingForm.process_loss_notes
      };

      if (editingBatch) {
        // UPDATE existing batch (PUT request)
        await axios.put(`${API}/manufacturing/batch/${editingBatch.id}`, batchData, { withCredentials: true });
        toast.success("Manufacturing batch updated successfully");
      } else {
        // CREATE new batch (POST request)
        await axios.post(`${API}/manufacturing/batch`, batchData, { withCredentials: true });
        toast.success("Manufacturing batch created successfully");
      }
      
      // Reset form state
      setManufacturingForm({
        inputs: [],
        output_product_id: "",
        output_quantity: 0,
        process_loss: 0,
        process_loss_notes: ""
      });
      setInputItems([{ product_id: "", quantity: 0 }]);
      setEditingBatch(null);  // Clear editing state
      setIsManufacturingSheetOpen(false);
      fetchManufacturingBatches();
      fetchInventory();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${editingBatch ? 'update' : 'create'} manufacturing batch`);
    }
  };

  const addInputItem = () => {
    setInputItems([...inputItems, { product_id: "", quantity: 0 }]);
  };

  const removeInputItem = (index) => {
    if (inputItems.length > 1) {
      setInputItems(inputItems.filter((_, i) => i !== index));
    }
  };

  const updateInputItem = (index, field, value) => {
    const updated = [...inputItems];
    updated[index][field] = field === 'quantity' ? parseFloat(value) || 0 : value;
    setInputItems(updated);
  };

  // Fetch saved challans
  const fetchSavedChallans = async () => {
    try {
      const params = new URLSearchParams();
      if (challanFilters.start_date) params.append('start_date', challanFilters.start_date);
      if (challanFilters.end_date) params.append('end_date', challanFilters.end_date);
      if (challanFilters.party_name) params.append('party_name', challanFilters.party_name);
      if (challanFilters.challan_number) params.append('challan_number', challanFilters.challan_number);
      
      const response = await axios.get(`${API}/challans?${params.toString()}`, { withCredentials: true });
      setSavedChallans(response.data.challans || []);
    } catch (error) {
      console.error("Failed to fetch saved challans:", error);
    }
  };

  // Calculate dashboard stats
  const stats = {
    totalProducts: products.length,
    totalStock: inventory.reduce((sum, item) => sum + item.current_stock, 0),
    lowStockItems: inventory.filter(item => item.current_stock > 0 && item.current_stock < 500),
    outOfStockItems: inventory.filter(item => item.current_stock === 0),
  };

  // Download Excel
  const handleDownload = () => {
    if (inventory.length === 0) {
      toast.error("No inventory data to download");
      return;
    }

    try {
      const dateStr = new Date().toISOString().split('T')[0];
      const excelData = inventory.map((item) => ({
        "Product Name": item.product_name,
        "Category": item.category,
        "Vendor": item.vendor,
        "In Stock (kg)": item.in_stock,
        "Out Stock (kg)": item.out_stock,
        "Current Stock (kg)": item.current_stock,
        "Date": dateStr,
      }));

      const ws = XLSX.utils.json_to_sheet(excelData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Inventory");
      XLSX.writeFile(wb, `stock_${dateStr}.xlsx`);
      toast.success("Excel file downloaded successfully");
    } catch (error) {
      console.error("Error downloading Excel:", error);
      toast.error("Failed to download Excel file");
    }
  };

  const currentDate = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  return (
    <div className="dashboard-container min-h-screen bg-stone-50">
      <div className="max-w-7xl mx-auto p-6 md:p-8 lg:p-12 relative z-10">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-12 h-12 bg-lime-700 rounded-xl flex items-center justify-center shadow-md shadow-lime-900/10">
                <Wheat className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-4xl font-outfit font-semibold tracking-tight text-stone-900">
                  Inventory Management
                </h1>
                <p className="text-sm font-public-sans text-stone-500 mt-1">{currentDate}</p>
              </div>
            </div>
            
            {currentUser && (
              <div className="flex items-center gap-4">
                {/* Language Selector */}
                <div className="relative">
                  <select
                    value={localStorage.getItem('preferred_language') || 'en'}
                    onChange={(e) => {
                      localStorage.setItem('preferred_language', e.target.value);
                      window.location.reload();
                    }}
                    className="appearance-none pl-8 pr-6 py-2 bg-white border border-stone-200 rounded-lg text-sm font-medium text-stone-700 cursor-pointer hover:bg-stone-50 focus:outline-none focus:ring-2 focus:ring-lime-500"
                    data-testid="dashboard-language-selector"
                  >
                    <option value="en">English</option>
                    <option value="hi">हिंदी</option>
                    <option value="pa">ਪੰਜਾਬੀ</option>
                  </select>
                  <Globe className="absolute left-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-stone-500 pointer-events-none" />
                </div>
                <div className="flex items-center gap-2 px-4 py-2 bg-lime-50 border border-lime-200 rounded-lg">
                  <Building2 className="w-5 h-5 text-lime-700" />
                  <div>
                    <p className="text-xs text-stone-500">{t('dashboard.currentWarehouse')}</p>
                    <p className="text-sm font-semibold text-lime-800">{currentWarehouse}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 px-4 py-2 bg-white border border-stone-200 rounded-lg">
                  <UserCircle className="w-5 h-5 text-stone-500" />
                  <div>
                    <p className="text-sm font-medium text-stone-900">{currentUser.name}</p>
                    <p className="text-xs text-stone-500 capitalize">{currentUser.role}</p>
                  </div>
                </div>
                <Button
                  onClick={handleLogout}
                  variant="outline"
                  className="border border-stone-300 bg-white hover:bg-stone-50 text-stone-700"
                  data-testid="logout-btn"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  {t('nav.logout')}
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className={`grid w-full ${currentUser?.role === 'admin' ? 'grid-cols-10' : 'grid-cols-6'} mb-8 bg-white border border-stone-200 p-1 rounded-lg shadow-sm`}>
            <TabsTrigger value="dashboard" data-testid="dashboard-tab" className="flex items-center gap-2 data-[state=active]:bg-lime-700 data-[state=active]:text-white rounded-md">
              <LayoutDashboard className="w-4 h-4" />{t('nav.dashboard')}
            </TabsTrigger>
            <TabsTrigger value="master" data-testid="master-tab" className="flex items-center gap-2 data-[state=active]:bg-lime-700 data-[state=active]:text-white rounded-md">
              <Archive className="w-4 h-4" />{t('nav.masterData')}
            </TabsTrigger>
            <TabsTrigger value="transactions" data-testid="transactions-tab" className="flex items-center gap-2 data-[state=active]:bg-lime-700 data-[state=active]:text-white rounded-md">
              <ArrowUpCircle className="w-4 h-4" />{t('nav.transactions')}
            </TabsTrigger>
            <TabsTrigger value="inventory" data-testid="inventory-tab" className="flex items-center gap-2 data-[state=active]:bg-lime-700 data-[state=active]:text-white rounded-md">
              <Package className="w-4 h-4" />{t('nav.inventory')}
            </TabsTrigger>
            <TabsTrigger value="manufacturing" data-testid="manufacturing-tab" className="flex items-center gap-2 data-[state=active]:bg-lime-700 data-[state=active]:text-white rounded-md">
              <Factory className="w-4 h-4" />{t('nav.manufacturing')}
            </TabsTrigger>
            <TabsTrigger value="reports" data-testid="reports-tab" className="flex items-center gap-2 data-[state=active]:bg-lime-700 data-[state=active]:text-white rounded-md">
              <FileText className="w-4 h-4" />{t('nav.reports')}
            </TabsTrigger>
            {currentUser?.role === 'admin' && (
              <>
                <TabsTrigger value="users" data-testid="users-tab" className="flex items-center gap-2 data-[state=active]:bg-lime-700 data-[state=active]:text-white rounded-md">
                  <Users className="w-4 h-4" />{t('nav.users')}
                </TabsTrigger>
                <TabsTrigger value="audit" data-testid="audit-tab" className="flex items-center gap-2 data-[state=active]:bg-lime-700 data-[state=active]:text-white rounded-md">
                  <ClipboardList className="w-4 h-4" />Logs
                </TabsTrigger>
                <TabsTrigger value="backup" data-testid="backup-tab" className="flex items-center gap-2 data-[state=active]:bg-lime-700 data-[state=active]:text-white rounded-md">
                  <Database className="w-4 h-4" />{t('nav.backup')}
                </TabsTrigger>
              </>
            )}
          </TabsList>

          {/* Dashboard Tab */}
          <TabsContent value="dashboard" className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <Card className="bg-white rounded-xl border border-stone-200 shadow-sm hover:shadow-md transition-shadow" data-testid="total-products-card">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-public-sans font-medium text-stone-500 uppercase tracking-wider">
                    Total Products
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between">
                    <p className="text-3xl font-outfit font-bold text-stone-900">{stats.totalProducts}</p>
                    <div className="w-12 h-12 bg-lime-100 rounded-lg flex items-center justify-center">
                      <Package className="w-6 h-6 text-lime-700" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-white rounded-xl border border-stone-200 shadow-sm hover:shadow-md transition-shadow">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-public-sans font-medium text-stone-500 uppercase tracking-wider">
                    Total Stock
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-3xl font-outfit font-bold text-stone-900">{stats.totalStock.toFixed(2)}</p>
                      <p className="text-xs text-stone-500 mt-1">kg</p>
                    </div>
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                      <TrendingUp className="w-6 h-6 text-blue-700" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-white rounded-xl border border-stone-200 shadow-sm hover:shadow-md transition-shadow">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-public-sans font-medium text-stone-500 uppercase tracking-wider">
                    Low Stock Alert
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between">
                    <p className="text-3xl font-outfit font-bold text-amber-700">{stats.lowStockItems.length}</p>
                    <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center">
                      <AlertTriangle className="w-6 h-6 text-amber-700" />
                    </div>
                  </div>
                  <p className="text-xs text-stone-500 mt-2">Below 500 kg threshold</p>
                </CardContent>
              </Card>

              <Card className="bg-white rounded-xl border border-stone-200 shadow-sm hover:shadow-md transition-shadow">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-public-sans font-medium text-stone-500 uppercase tracking-wider">
                    Out of Stock
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between">
                    <p className="text-3xl font-outfit font-bold text-rose-700">{stats.outOfStockItems.length}</p>
                    <div className="w-12 h-12 bg-rose-100 rounded-lg flex items-center justify-center">
                      <TrendingDown className="w-6 h-6 text-rose-700" />
                    </div>
                  </div>
                  <p className="text-xs text-stone-500 mt-2">Stock = 0 kg</p>
                </CardContent>
              </Card>
            </div>

            {/* Low Stock Alerts */}
            {stats.lowStockItems.length > 0 && (
              <Card className="bg-white rounded-xl border border-stone-200 shadow-sm">
                <CardHeader>
                  <CardTitle className="text-xl font-outfit font-semibold text-stone-900">Low Stock Alerts</CardTitle>
                  <CardDescription>Products below 500 kg threshold requiring attention</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {stats.lowStockItems.map((item) => (
                      <div key={item.id} className="flex items-center justify-between p-4 bg-amber-50 border border-amber-200 rounded-lg">
                        <div>
                          <p className="font-medium text-stone-900">{item.product_name}</p>
                          <p className="text-xs text-stone-500">{item.category} • Threshold: {item.threshold || 500} kg</p>
                        </div>
                        <div className="text-left">
                          <p className="text-xl font-bold text-amber-700">{item.current_stock.toFixed(2)} kg</p>
                          <p className="text-xs text-amber-600">Current Stock</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Master Data Tab */}
          <TabsContent value="master" className="space-y-6">
            <Tabs value={masterTab} onValueChange={setMasterTab}>
              <TabsList className="bg-white border border-stone-200 p-1 rounded-lg">
                <TabsTrigger value="products" className="data-[state=active]:bg-lime-700 data-[state=active]:text-white rounded-md">
                  {t('masterData.products')}
                </TabsTrigger>
                <TabsTrigger value="vendors" className="data-[state=active]:bg-lime-700 data-[state=active]:text-white rounded-md">
                  {t('masterData.vendors')}
                </TabsTrigger>
                {currentUser?.role === 'admin' && (
                  <>
                    <TabsTrigger value="categories" className="data-[state=active]:bg-lime-700 data-[state=active]:text-white rounded-md">
                      {t('masterData.categories')}
                    </TabsTrigger>
                    <TabsTrigger value="warehouses" className="data-[state=active]:bg-lime-700 data-[state=active]:text-white rounded-md">
                      {t('masterData.warehouses')}
                    </TabsTrigger>
                  </>
                )}
                {hasPermission('manager') && (
                  <TabsTrigger value="archive" className="data-[state=active]:bg-lime-700 data-[state=active]:text-white rounded-md" data-testid="archive-master-tab">
                    <Archive className="w-4 h-4 mr-1" />
                    Archive
                  </TabsTrigger>
                )}
              </TabsList>

              {/* Products Master */}
              <TabsContent value="products" className="space-y-4 mt-6">
                <div className="flex justify-between items-center gap-4">
                  <h3 className="text-lg font-outfit font-semibold text-stone-900">Products Master List</h3>
                  <div className="flex items-center gap-3">
                    <SearchInput
                      value={productSearch}
                      onChange={setProductSearch}
                      placeholder="Search products..."
                      className="w-64"
                    />
                    {hasPermission('manager') && selectedProductsForArchive.length > 0 && (
                      <Button
                        onClick={() => handleArchiveItems(selectedProductsForArchive, 'product')}
                        variant="outline"
                        className="border-amber-500 text-amber-700 hover:bg-amber-50"
                        disabled={archiveLoading}
                        data-testid="archive-selected-products-btn"
                      >
                        <Archive className="w-4 h-4 mr-2" />
                        Archive ({selectedProductsForArchive.length})
                      </Button>
                    )}
                    {hasPermission('manager') && (
                      <Sheet open={isProductSheetOpen} onOpenChange={setIsProductSheetOpen}>
                        <SheetTrigger asChild>
                          <Button
                            data-testid="add-product-master-btn"
                            onClick={() => {
                              setEditingProduct(null);
                              setProductForm({ product_name: "", category: "" });
                            }}
                            className="bg-lime-700 text-white hover:bg-lime-800"
                          >
                            <Plus className="w-4 h-4 mr-2" />Add Product
                          </Button>
                        </SheetTrigger>
                      <SheetContent className="overflow-y-auto">
                        <SheetHeader>
                          <SheetTitle>{editingProduct ? "Edit Product" : "Add New Product"}</SheetTitle>
                        </SheetHeader>
                        <form onSubmit={handleProductSubmit} className="space-y-6 mt-6">
                          <div className="space-y-2">
                            <Label htmlFor="product_name">{t('masterData.productName')} *</Label>
                            <Input
                              id="product_name"
                              value={productForm.product_name}
                              onChange={(e) => setProductForm({ ...productForm, product_name: e.target.value })}
                              required
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="category">{t('masterData.category')} *</Label>
                            <Select value={productForm.category} onValueChange={(value) => setProductForm({ ...productForm, category: value })}>
                              <SelectTrigger>
                                <SelectValue placeholder="Select category" />
                              </SelectTrigger>
                              <SelectContent>
                                {categories.map((cat) => (
                                  <SelectItem key={cat.id} value={cat.name}>{cat.name}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <SheetFooter>
                            <Button type="submit" className="bg-lime-700 text-white hover:bg-lime-800">
                              {editingProduct ? t('common.update') : t('common.add')} Product
                            </Button>
                          </SheetFooter>
                        </form>
                      </SheetContent>
                    </Sheet>
                    )}
                  </div>
                </div>

                <Card>
                  <CardContent className="p-0">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          {hasPermission('manager') && (
                            <TableHead className="w-10">
                              <input
                                type="checkbox"
                                className="rounded border-stone-300"
                                checked={filteredProducts.length > 0 && selectedProductsForArchive.length === filteredProducts.length}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setSelectedProductsForArchive(filteredProducts.map(p => p.id));
                                  } else {
                                    setSelectedProductsForArchive([]);
                                  }
                                }}
                                data-testid="select-all-products"
                              />
                            </TableHead>
                          )}
                          <SortableHeader column="product_name" label="Product Name" sortConfig={productSort} onSort={setProductSort} />
                          <SortableHeader column="category" label="Category" sortConfig={productSort} onSort={setProductSort} />
                          <TableHead className="text-left">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {paginatedProducts.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={hasPermission('manager') ? 4 : 3} className="text-center py-8 text-stone-500">
                              {productSearch ? `No products found matching "${productSearch}"` : "No products found. Add your first product to get started."}
                            </TableCell>
                          </TableRow>
                        ) : (
                          paginatedProducts.map((product) => (
                            <TableRow key={product.id} className={selectedProductsForArchive.includes(product.id) ? 'bg-amber-50' : ''}>
                              {hasPermission('manager') && (
                                <TableCell>
                                  <input
                                    type="checkbox"
                                    className="rounded border-stone-300"
                                    checked={selectedProductsForArchive.includes(product.id)}
                                    onChange={(e) => {
                                      if (e.target.checked) {
                                        setSelectedProductsForArchive([...selectedProductsForArchive, product.id]);
                                      } else {
                                        setSelectedProductsForArchive(selectedProductsForArchive.filter(id => id !== product.id));
                                      }
                                    }}
                                    data-testid={`product-checkbox-${product.id}`}
                                  />
                                </TableCell>
                              )}
                              <TableCell className="font-medium">{product.product_name}</TableCell>
                              <TableCell>
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800 border border-amber-200">
                                  {product.category}
                                </span>
                              </TableCell>
                              <TableCell>
                                <div className="flex items-center justify-end gap-1">
                                  <Button
                                    onClick={() => {
                                      setEditingProduct(product);
                                      setProductForm({ product_name: product.product_name, category: product.category });
                                      setIsProductSheetOpen(true);
                                    }}
                                    variant="ghost"
                                    size="icon"
                                    className="h-8 w-8"
                                    title="Edit Product"
                                  >
                                    <Edit2 className="w-4 h-4" />
                                  </Button>
                                  {currentUser?.role === 'admin' && (
                                    <Button
                                      onClick={() => handleDeleteProduct(product.id)}
                                      variant="ghost"
                                      size="icon"
                                      className="h-8 w-8 text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                                      title="Delete Product"
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </Button>
                                  )}
                                </div>
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </CardContent>
                  {filteredProducts.length > 0 && (
                    <TablePagination
                      currentPage={productPage}
                      totalPages={totalProductPages}
                      totalItems={filteredProducts.length}
                      pageSize={productPageSize}
                      onPageChange={setProductPage}
                      onPageSizeChange={(size) => {
                        setProductPageSize(size);
                        setProductPage(1);
                      }}
                      data-testid="product-pagination"
                    />
                  )}
                </Card>
              </TabsContent>

              {/* Vendors Master */}
              <TabsContent value="vendors" className="space-y-4 mt-6">
                <div className="flex justify-between items-center gap-4">
                  <h3 className="text-lg font-outfit font-semibold text-stone-900">Vendors Master List</h3>
                  <div className="flex items-center gap-3">
                    <SearchInput
                      value={vendorSearch}
                      onChange={setVendorSearch}
                      placeholder="Search vendors..."
                      className="w-64"
                    />
                    {hasPermission('manager') && selectedVendorsForArchive.length > 0 && (
                      <Button
                        onClick={() => handleArchiveItems(selectedVendorsForArchive, 'vendor')}
                        variant="outline"
                        className="border-amber-500 text-amber-700 hover:bg-amber-50"
                        disabled={archiveLoading}
                        data-testid="archive-selected-vendors-btn"
                      >
                        <Archive className="w-4 h-4 mr-2" />
                        Archive ({selectedVendorsForArchive.length})
                      </Button>
                    )}
                    {hasPermission('manager') && (
                      <Sheet open={isVendorSheetOpen} onOpenChange={setIsVendorSheetOpen}>
                        <SheetTrigger asChild>
                          <Button
                            data-testid="add-vendor-master-btn"
                            onClick={() => {
                              setEditingVendor(null);
                              setVendorForm({ vendor_name: "", contact: "" });
                            }}
                            className="bg-lime-700 text-white hover:bg-lime-800"
                          >
                            <Plus className="w-4 h-4 mr-2" />Add Vendor
                          </Button>
                        </SheetTrigger>
                      <SheetContent className="overflow-y-auto">
                        <SheetHeader>
                          <SheetTitle>{editingVendor ? "Edit Vendor" : "Add New Vendor"}</SheetTitle>
                        </SheetHeader>
                        <form onSubmit={handleVendorSubmit} className="space-y-6 mt-6">
                          <div className="space-y-2">
                            <Label htmlFor="vendor_name">Vendor Name *</Label>
                            <Input
                              id="vendor_name"
                              value={vendorForm.vendor_name}
                              onChange={(e) => setVendorForm({ ...vendorForm, vendor_name: e.target.value })}
                              required
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="contact">Contact</Label>
                            <Input
                              id="contact"
                              value={vendorForm.contact}
                              onChange={(e) => setVendorForm({ ...vendorForm, contact: e.target.value })}
                              placeholder="Phone or email"
                            />
                          </div>
                          <SheetFooter>
                            <Button type="submit" className="bg-lime-700 text-white hover:bg-lime-800">
                              {editingVendor ? "Update" : "Add"} Vendor
                            </Button>
                          </SheetFooter>
                        </form>
                      </SheetContent>
                    </Sheet>
                    )}
                  </div>
                </div>

                <Card>
                  <CardContent className="p-0">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          {hasPermission('manager') && (
                            <TableHead className="w-10">
                              <input
                                type="checkbox"
                                className="rounded border-stone-300"
                                checked={filteredVendors.length > 0 && selectedVendorsForArchive.length === filteredVendors.length}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setSelectedVendorsForArchive(filteredVendors.map(v => v.id));
                                  } else {
                                    setSelectedVendorsForArchive([]);
                                  }
                                }}
                                data-testid="select-all-vendors"
                              />
                            </TableHead>
                          )}
                          <SortableHeader column="vendor_name" label="Vendor Name" sortConfig={vendorSort} onSort={setVendorSort} />
                          <SortableHeader column="contact" label="Contact" sortConfig={vendorSort} onSort={setVendorSort} />
                          <TableHead className="text-left">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {paginatedVendors.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={hasPermission('manager') ? 4 : 3} className="text-center py-8 text-stone-500">
                              {vendorSearch ? `No vendors found matching "${vendorSearch}"` : "No vendors found. Add your first vendor to get started."}
                            </TableCell>
                          </TableRow>
                        ) : (
                          paginatedVendors.map((vendor) => (
                            <TableRow key={vendor.id} className={selectedVendorsForArchive.includes(vendor.id) ? 'bg-amber-50' : ''}>
                              {hasPermission('manager') && (
                                <TableCell>
                                  <input
                                    type="checkbox"
                                    className="rounded border-stone-300"
                                    checked={selectedVendorsForArchive.includes(vendor.id)}
                                    onChange={(e) => {
                                      if (e.target.checked) {
                                        setSelectedVendorsForArchive([...selectedVendorsForArchive, vendor.id]);
                                      } else {
                                        setSelectedVendorsForArchive(selectedVendorsForArchive.filter(id => id !== vendor.id));
                                      }
                                    }}
                                    data-testid={`vendor-checkbox-${vendor.id}`}
                                  />
                                </TableCell>
                              )}
                              <TableCell className="font-medium">{vendor.vendor_name}</TableCell>
                              <TableCell>{vendor.contact || "N/A"}</TableCell>
                              <TableCell>
                                <div className="flex items-center justify-end gap-1">
                                  <Button
                                    onClick={() => {
                                      setEditingVendor(vendor);
                                      setVendorForm({ vendor_name: vendor.vendor_name, contact: vendor.contact || "" });
                                      setIsVendorSheetOpen(true);
                                    }}
                                    variant="ghost"
                                    size="icon"
                                    className="h-8 w-8"
                                    title="Edit Vendor"
                                  >
                                    <Edit2 className="w-4 h-4" />
                                  </Button>
                                  {currentUser?.role === 'admin' && (
                                    <Button
                                      onClick={() => handleDeleteVendor(vendor.id)}
                                      variant="ghost"
                                      size="icon"
                                      className="h-8 w-8 text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                                      title="Delete Vendor"
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </Button>
                                  )}
                                </div>
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </CardContent>
                  {filteredVendors.length > 0 && (
                    <TablePagination
                      currentPage={vendorPage}
                      totalPages={totalVendorPages}
                      totalItems={filteredVendors.length}
                      pageSize={vendorPageSize}
                      onPageChange={setVendorPage}
                      onPageSizeChange={(size) => {
                        setVendorPageSize(size);
                        setVendorPage(1);
                      }}
                      data-testid="vendor-pagination"
                    />
                  )}
                </Card>
              </TabsContent>

              {/* Categories Master (Admin Only) */}
              {currentUser?.role === 'admin' && (
                <TabsContent value="categories" className="space-y-4 mt-6">
                  <div className="flex justify-between items-center">
                    <h3 className="text-lg font-outfit font-semibold text-stone-900">{t('masterData.categories')}</h3>
                    <Sheet open={isCategorySheetOpen} onOpenChange={setIsCategorySheetOpen}>
                      <SheetTrigger asChild>
                        <Button
                          data-testid="add-category-btn"
                          onClick={() => {
                            setEditingCategory(null);
                            setCategoryForm({ name: "", description: "" });
                          }}
                          className="bg-lime-700 text-white hover:bg-lime-800"
                        >
                          <Plus className="w-4 h-4 mr-2" />{t('masterData.addCategory')}
                        </Button>
                      </SheetTrigger>
                      <SheetContent className="overflow-y-auto">
                        <SheetHeader>
                          <SheetTitle>{editingCategory ? t('common.edit') : t('common.add')} Category</SheetTitle>
                        </SheetHeader>
                        <form onSubmit={handleCategorySubmit} className="space-y-6 mt-6">
                          <div className="space-y-2">
                            <Label htmlFor="category_name">Category Name *</Label>
                            <Input
                              id="category_name"
                              value={categoryForm.name}
                              onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })}
                              placeholder="e.g., Millets, Flours, Grains"
                              required
                              data-testid="category-name-input"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="category_desc">Description</Label>
                            <Input
                              id="category_desc"
                              value={categoryForm.description}
                              onChange={(e) => setCategoryForm({ ...categoryForm, description: e.target.value })}
                              placeholder="Optional description"
                              data-testid="category-desc-input"
                            />
                          </div>
                          <SheetFooter>
                            <Button type="submit" className="bg-lime-700 text-white hover:bg-lime-800" data-testid="save-category-btn">
                              {editingCategory ? t('common.update') : t('common.add')} Category
                            </Button>
                          </SheetFooter>
                        </form>
                      </SheetContent>
                    </Sheet>
                  </div>

                  <Card>
                    <CardContent className="p-0">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Category Name</TableHead>
                            <TableHead>Description</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead className="text-left">{t('masterData.actions')}</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {categories.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={4} className="text-center py-8 text-stone-500">
                                {t('common.noData')}
                              </TableCell>
                            </TableRow>
                          ) : (
                            categories.map((category) => (
                              <TableRow key={category.id} data-testid={`category-row-${category.id}`}>
                                <TableCell className="font-medium">
                                  <div className="flex items-center gap-2">
                                    <FolderTree className="w-4 h-4 text-amber-600" />
                                    {category.name}
                                  </div>
                                </TableCell>
                                <TableCell className="text-stone-500">{category.description || "-"}</TableCell>
                                <TableCell>
                                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                    category.is_active !== false
                                      ? 'bg-lime-100 text-lime-800 border border-lime-200'
                                      : 'bg-stone-100 text-stone-600 border border-stone-200'
                                  }`}>
                                    {category.is_active !== false ? 'Active' : 'Inactive'}
                                  </span>
                                </TableCell>
                                <TableCell className="text-left">
                                  <Button
                                    onClick={() => {
                                      setEditingCategory(category);
                                      setCategoryForm({ name: category.name, description: category.description || "" });
                                      setIsCategorySheetOpen(true);
                                    }}
                                    variant="ghost"
                                    size="sm"
                                    className="mr-2"
                                    data-testid={`edit-category-${category.id}`}
                                  >
                                    <Edit2 className="w-4 h-4" />
                                  </Button>
                                  <Button
                                    onClick={() => handleDeleteCategory(category.id)}
                                    variant="ghost"
                                    size="sm"
                                    className="text-rose-600 hover:text-rose-700"
                                    data-testid={`delete-category-${category.id}`}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </TableCell>
                              </TableRow>
                            ))
                          )}
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>
                </TabsContent>
              )}

              {/* Warehouses Master (Admin Only) */}
              {currentUser?.role === 'admin' && (
                <TabsContent value="warehouses" className="space-y-4 mt-6">
                  <WarehouseManagement />
                </TabsContent>
              )}
              
              {/* Archive Tab */}
              {hasPermission('manager') && (
                <TabsContent value="archive" className="space-y-4 mt-6">
                  <ArchiveManager currentUserRole={currentUser?.role} />
                </TabsContent>
              )}
            </Tabs>
          </TabsContent>

          {/* Transactions Tab */}
          <TabsContent value="transactions" className="space-y-6">
            <div className="flex flex-col gap-4">
              <div className="flex justify-between items-center">
                <div>
                  <h3 className="text-lg font-outfit font-semibold text-stone-900">Stock Transactions</h3>
                  <p className="text-sm text-stone-500">Record stock in and out movements</p>
                </div>
                <div className="flex gap-3">
                  {/* View Saved Challans Button */}
                  <Button
                    variant="outline"
                    onClick={() => setShowSavedChallansModal(true)}
                    className="border-blue-500 text-blue-700 hover:bg-blue-50"
                    data-testid="view-saved-challans-btn"
                  >
                    <FileText className="w-4 h-4 mr-2" />
                    View Saved Challans
                  </Button>
                  
                  {selectedTransactionIds.length > 0 && (
                    <Button
                      onClick={() => {
                        const selectedTrans = transactions.filter(t => 
                          selectedTransactionIds.includes(t.id) && t.transaction_type === 'out'
                        );
                        if (selectedTrans.length > 0) {
                          setSelectedChallanTransactions(selectedTrans);
                          setShowChallanModal(true);
                        }
                      }}
                      className="bg-amber-600 text-white hover:bg-amber-700"
                      data-testid="print-multi-challan-btn"
                    >
                      <Truck className="w-4 h-4 mr-2" />
                      Print Challan ({selectedTransactionIds.length})
                    </Button>
                  )}
                  {hasPermission('manager') && (
                    <Sheet open={isTransactionSheetOpen} onOpenChange={setIsTransactionSheetOpen}>
                      <SheetTrigger asChild>
                        <Button
                          data-testid="add-transaction-btn"
                          onClick={() => setTransactionForm({
                            product_id: "",
                            vendor_id: "",
                            transaction_type: "in",
                            quantity: 0,
                            notes: "",
                            driver_name: "",
                            driver_contact: "",
                            vehicle_number: ""
                          })}
                          className="bg-lime-700 text-white hover:bg-lime-800"
                        >
                          <Plus className="w-4 h-4 mr-2" />{t('transactions.recordTransaction')}
                        </Button>
                      </SheetTrigger>
                  <SheetContent className="overflow-y-auto">
                    <SheetHeader>
                      <SheetTitle>{t('transactions.recordTransaction')}</SheetTitle>
                      <SheetDescription>{t('transactions.subtitle')}</SheetDescription>
                    </SheetHeader>
                    <form onSubmit={handleTransactionSubmit} className="space-y-6 mt-6">
                      <div className="space-y-2">
                        <Label htmlFor="trans_product">Product *</Label>
                        <Select value={transactionForm.product_id} onValueChange={(value) => setTransactionForm({ ...transactionForm, product_id: value })}>
                          <SelectTrigger>
                            <SelectValue placeholder="Select product" />
                          </SelectTrigger>
                          <SelectContent>
                            {products.map((p) => (
                              <SelectItem key={p.id} value={p.id}>{p.product_name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="trans_vendor">Vendor *</Label>
                        <Select value={transactionForm.vendor_id} onValueChange={(value) => setTransactionForm({ ...transactionForm, vendor_id: value })}>
                          <SelectTrigger>
                            <SelectValue placeholder="Select vendor" />
                          </SelectTrigger>
                          <SelectContent>
                            {vendors.map((v) => (
                              <SelectItem key={v.id} value={v.id}>{v.vendor_name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="trans_type">{t('transactions.type')} *</Label>
                      <Select value={transactionForm.transaction_type} onValueChange={(value) => setTransactionForm({ ...transactionForm, transaction_type: value })}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="in">{t('transactions.stockIn')}</SelectItem>
                          <SelectItem value="out">{t('transactions.stockOut')}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="trans_quantity">{t('transactions.quantity')} ({t('common.kg')}) *</Label>
                      <Input
                        id="trans_quantity"
                        type="number"
                        step="0.01"
                        min="0.01"
                        value={transactionForm.quantity}
                        onChange={(e) => setTransactionForm({ ...transactionForm, quantity: parseFloat(e.target.value) || 0 })}
                        required
                        data-testid="trans-quantity-input"
                      />
                    </div>
                    
                    {/* Logistics Fields Section */}
                    <div className="border-t border-stone-200 pt-4 mt-4">
                      <h4 className="text-sm font-semibold text-stone-700 mb-3 flex items-center gap-2">
                        <Truck className="w-4 h-4" /> Logistics Details *
                      </h4>
                      <div className="space-y-4">
                        <div className="space-y-2">
                          <Label htmlFor="driver_name" className="flex items-center gap-2">
                            <User className="w-3 h-3" /> {t('transactions.driverName')} *
                          </Label>
                          <Input
                            id="driver_name"
                            value={transactionForm.driver_name}
                            onChange={(e) => setTransactionForm({ ...transactionForm, driver_name: e.target.value })}
                            placeholder="Enter driver's name"
                            required
                            data-testid="driver-name-input"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="driver_contact" className="flex items-center gap-2">
                            <Phone className="w-3 h-3" /> {t('transactions.driverContact')} *
                          </Label>
                          <Input
                            id="driver_contact"
                            value={transactionForm.driver_contact}
                            onChange={(e) => setTransactionForm({ ...transactionForm, driver_contact: e.target.value })}
                            placeholder="Enter contact number"
                            required
                            data-testid="driver-contact-input"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="vehicle_number" className="flex items-center gap-2">
                            <Truck className="w-3 h-3" /> {t('transactions.vehicleNumber')} *
                          </Label>
                          <Input
                            id="vehicle_number"
                            value={transactionForm.vehicle_number}
                            onChange={(e) => setTransactionForm({ ...transactionForm, vehicle_number: e.target.value })}
                            placeholder="e.g., PB 02 AB 1234"
                            required
                            data-testid="vehicle-number-input"
                          />
                        </div>
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="trans_notes">{t('transactions.notes')}</Label>
                      <Input
                        id="trans_notes"
                        value={transactionForm.notes}
                        onChange={(e) => setTransactionForm({ ...transactionForm, notes: e.target.value })}
                        placeholder="Optional notes"
                        data-testid="trans-notes-input"
                      />
                    </div>
                    <SheetFooter>
                      <Button type="submit" className="bg-lime-700 text-white hover:bg-lime-800" data-testid="submit-transaction-btn">
                        {t('transactions.recordTransaction')}
                      </Button>
                    </SheetFooter>
                  </form>
                </SheetContent>
              </Sheet>
                )}
              </div>
            </div>
              
              {/* Search Bar for Transactions */}
              <div className="flex items-center gap-4">
                <SearchInput
                  value={transactionSearch}
                  onChange={setTransactionSearch}
                  placeholder="Search by product, vendor, driver, vehicle..."
                  className="w-96"
                />
                <span className="text-sm text-stone-500">
                  {selectedTransactionIds.length > 0 && `${selectedTransactionIds.length} selected for challan`}
                </span>
              </div>
            </div>

            <Card>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-10">
                        <input
                          type="checkbox"
                          className="rounded border-stone-300"
                          data-testid="select-all-transactions"
                          onChange={(e) => {
                            if (e.target.checked) {
                              const outTransIds = filteredTransactions
                                .filter(t => t.transaction_type === 'out')
                                .map(t => t.id);
                              setSelectedTransactionIds(outTransIds);
                            } else {
                              setSelectedTransactionIds([]);
                            }
                          }}
                          checked={
                            filteredTransactions.filter(t => t.transaction_type === 'out').length > 0 &&
                            selectedTransactionIds.length === filteredTransactions.filter(t => t.transaction_type === 'out').length
                          }
                        />
                      </TableHead>
                      <SortableHeader column="transaction_date" label="Date & Time" sortConfig={transactionSort} onSort={setTransactionSort} className="w-40" />
                      <SortableHeader column="product_name" label="Product" sortConfig={transactionSort} onSort={setTransactionSort} className="w-32" />
                      <SortableHeader column="vendor_name" label="Vendor" sortConfig={transactionSort} onSort={setTransactionSort} className="w-40" />
                      <TableHead className="w-16">Type</TableHead>
                      <SortableHeader column="quantity" label="Qty (kg)" sortConfig={transactionSort} onSort={setTransactionSort} className="text-left w-24" />
                      <TableHead className="min-w-[160px]">Notes</TableHead>
                      <TableHead className="text-center w-16">Status</TableHead>
                      <TableHead className="text-center w-14">Edit</TableHead>
                      <TableHead className="text-center w-14">Challan</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paginatedTransactions.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={10} className="text-center py-8 text-stone-500">
                          {transactionSearch ? `No transactions found matching "${transactionSearch}"` : "No transactions recorded yet."}
                        </TableCell>
                      </TableRow>
                    ) : (
                      paginatedTransactions.map((trans) => (
                        <TableRow 
                          key={trans.id} 
                          className={`transition-colors duration-500 ${
                            recentlyUpdatedTransactionId === trans.id 
                              ? 'bg-lime-100 border-l-4 border-l-lime-500' 
                              : selectedTransactionIds.includes(trans.id) 
                                ? 'bg-amber-50' 
                                : ''
                          }`}
                          data-testid={`transaction-row-${trans.id}`}
                        >
                          <TableCell>
                            {trans.transaction_type === 'out' && (
                              <input
                                type="checkbox"
                                className="rounded border-stone-300"
                                checked={selectedTransactionIds.includes(trans.id)}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setSelectedTransactionIds([...selectedTransactionIds, trans.id]);
                                  } else {
                                    setSelectedTransactionIds(selectedTransactionIds.filter(id => id !== trans.id));
                                  }
                                }}
                                data-testid={`transaction-checkbox-${trans.id}`}
                              />
                            )}
                          </TableCell>
                          <TableCell className="font-medium whitespace-nowrap text-sm">
                            {new Date(trans.transaction_date).toLocaleDateString('en-IN', {
                              day: '2-digit',
                              month: 'short',
                              year: 'numeric'
                            })}
                            <span className="text-stone-400 ml-1">
                              {new Date(trans.transaction_date).toLocaleTimeString('en-IN', {
                                hour: '2-digit',
                                minute: '2-digit',
                                hour12: true
                              })}
                            </span>
                          </TableCell>
                          <TableCell className="font-medium truncate max-w-[140px]" title={trans.product_name}>
                            {trans.product_name}
                          </TableCell>
                          <TableCell className="truncate max-w-[180px]" title={trans.vendor_name}>
                            {trans.vendor_name}
                          </TableCell>
                          <TableCell>
                            {trans.transaction_type === 'in' ? (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-lime-100 text-lime-800 border border-lime-200">
                                <ArrowUpCircle className="w-3 h-3 mr-1" />In
                              </span>
                            ) : (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-rose-100 text-rose-800 border border-rose-200">
                                <ArrowDownCircle className="w-3 h-3 mr-1" />Out
                              </span>
                            )}
                          </TableCell>
                          <TableCell className="text-left font-mono font-medium tabular-nums">
                            {trans.quantity.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </TableCell>
                          {/* Notes Column - User-entered notes only */}
                          <TableCell className="text-stone-600 text-sm">
                            <span className="truncate block max-w-[180px]" title={trans.notes || '-'}>
                              {trans.notes || "-"}
                            </span>
                          </TableCell>
                          {/* Status Column - System-generated status (Edited badge) */}
                          <TableCell className="text-center">
                            {trans.is_edited ? (
                              <EditedBadge 
                                transaction={trans} 
                                onClick={() => {
                                  setSelectedTransactionForHistory(trans);
                                  setShowTransactionHistory(true);
                                }}
                              />
                            ) : (
                              <span className="text-stone-300">—</span>
                            )}
                          </TableCell>
                          {/* Edit Column - Always visible for managers */}
                          <TableCell className="text-center">
                            {hasPermission('manager') ? (
                              <Button
                                onClick={() => {
                                  setEditingTransaction(trans);
                                  setShowTransactionEditSheet(true);
                                }}
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 mx-auto"
                                title="Edit Transaction"
                                data-testid={`edit-transaction-btn-${trans.id}`}
                              >
                                <Edit2 className="w-4 h-4" />
                              </Button>
                            ) : (
                              <span className="text-stone-300">—</span>
                            )}
                          </TableCell>
                          {/* Challan Column - Only for Stock Out transactions */}
                          <TableCell className="text-center">
                            {trans.transaction_type === 'out' ? (
                              <Button
                                onClick={() => {
                                  setSelectedChallanTransactions([trans]);
                                  setShowChallanModal(true);
                                }}
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 mx-auto text-amber-700 hover:text-amber-800 hover:bg-amber-50"
                                title="Print Challan"
                                data-testid={`challan-btn-${trans.id}`}
                              >
                                <Truck className="w-4 h-4" />
                              </Button>
                            ) : (
                              <span className="text-stone-300">—</span>
                            )}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </CardContent>
              {filteredTransactions.length > 0 && (
                <TablePagination
                  currentPage={transactionPage}
                  totalPages={totalTransactionPages}
                  totalItems={filteredTransactions.length}
                  pageSize={transactionPageSize}
                  onPageChange={setTransactionPage}
                  onPageSizeChange={(size) => {
                    setTransactionPageSize(size);
                    setTransactionPage(1);
                  }}
                  data-testid="transaction-pagination"
                />
              )}
            </Card>
          </TabsContent>

          {/* Inventory Tab */}
          <TabsContent value="inventory" className="space-y-6">
            <div className="flex flex-col gap-4">
              <div className="flex justify-between items-center">
                <div>
                  <h3 className="text-lg font-outfit font-semibold text-stone-900">Current Warehouse Inventory</h3>
                  <p className="text-sm text-stone-500">Latest available stock (Opening + In - Out)</p>
                </div>
                <div className="flex gap-3">
                  {hasPermission('manager') && (
                    <Button
                      onClick={() => setShowOpeningStockModal(true)}
                      variant="outline"
                      className="border border-blue-300 bg-blue-50 hover:bg-blue-100 text-blue-700"
                    >
                      <Package className="w-4 h-4 mr-2" />Set Opening Stock
                    </Button>
                  )}
                  <Button
                    data-testid="print-stock-btn"
                    onClick={() => setShowPrintModal(true)}
                    variant="outline"
                    className="border border-stone-300 bg-white hover:bg-stone-50 text-stone-700"
                  >
                    <Printer className="w-4 h-4 mr-2" />Print Stock
                  </Button>
                  <Button
                    data-testid="download-excel-btn"
                    onClick={handleDownload}
                    variant="outline"
                    className="border border-stone-300 bg-white hover:bg-stone-50 text-stone-700"
                  >
                    <Download className="w-4 h-4 mr-2" />Download Excel
                  </Button>
                </div>
              </div>
              
              {/* Search Bar for Inventory */}
              <SearchInput
                value={inventorySearch}
                onChange={setInventorySearch}
                placeholder="Search by product, category, vendor..."
                className="w-96"
              />
            </div>

            <Card>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <SortableHeader column="product_name" label="Product Name" sortConfig={inventorySort} onSort={setInventorySort} />
                      <SortableHeader column="category" label="Category" sortConfig={inventorySort} onSort={setInventorySort} />
                      <SortableHeader column="vendor" label="Latest Vendor" sortConfig={inventorySort} onSort={setInventorySort} />
                      <SortableHeader column="opening_stock" label="Opening (kg)" sortConfig={inventorySort} onSort={setInventorySort} className="text-left" />
                      <SortableHeader column="in_stock" label="In Stock (kg)" sortConfig={inventorySort} onSort={setInventorySort} className="text-left" />
                      <SortableHeader column="out_stock" label="Out Stock (kg)" sortConfig={inventorySort} onSort={setInventorySort} className="text-left" />
                      <SortableHeader column="current_stock" label="Current Stock (kg)" sortConfig={inventorySort} onSort={setInventorySort} className="text-left" />
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paginatedInventory.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} className="text-center py-8 text-stone-500">
                          {inventorySearch ? `No inventory found matching "${inventorySearch}"` : "No inventory data available."}
                        </TableCell>
                      </TableRow>
                    ) : (
                      paginatedInventory.map((item) => {
                        let status = "Healthy";
                        let statusColor = "bg-lime-100 text-lime-800 border-lime-200";
                        
                        if (item.current_stock === 0) {
                          status = "Out of Stock";
                          statusColor = "bg-rose-100 text-rose-800 border-rose-200";
                        } else if (item.current_stock < 500) {
                          status = "Low Stock";
                          statusColor = "bg-amber-100 text-amber-800 border-amber-200";
                        }

                        return (
                          <TableRow key={item.id}>
                            <TableCell className="font-medium">{item.product_name}</TableCell>
                            <TableCell>
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800 border border-amber-200">
                                {item.category}
                              </span>
                            </TableCell>
                            <TableCell>{item.vendor}</TableCell>
                            <TableCell className="text-left text-blue-600">{(item.opening_stock || 0).toFixed(2)}</TableCell>
                            <TableCell className="text-left text-lime-600">{item.in_stock.toFixed(2)}</TableCell>
                            <TableCell className="text-left text-rose-600">{item.out_stock.toFixed(2)}</TableCell>
                            <TableCell className="text-left font-semibold">{item.current_stock.toFixed(2)}</TableCell>
                            <TableCell>
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${statusColor}`}>
                                {status}
                              </span>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </CardContent>
              {filteredInventory.length > 0 && (
                <TablePagination
                  currentPage={inventoryPage}
                  totalPages={totalInventoryPages}
                  totalItems={filteredInventory.length}
                  pageSize={inventoryPageSize}
                  onPageChange={setInventoryPage}
                  onPageSizeChange={(size) => {
                    setInventoryPageSize(size);
                    setInventoryPage(1);
                  }}
                  data-testid="inventory-pagination"
                />
              )}
            </Card>
          </TabsContent>

          {/* Reports Tab - Enhanced with Sub-tabs */}
          <TabsContent value="reports" className="space-y-6">
            <Tabs defaultValue="inventory-reports" className="w-full">
              <div className="flex justify-between items-center mb-4">
                <div>
                  <h3 className="text-lg font-outfit font-semibold text-stone-900">Reports & Statements</h3>
                  <p className="text-sm text-stone-500">Comprehensive reporting and analytics</p>
                </div>
              </div>
              
              <TabsList className="grid grid-cols-3 w-full max-w-md mb-6 bg-stone-100 p-1 rounded-lg">
                <TabsTrigger value="inventory-reports" className="flex items-center gap-2 data-[state=active]:bg-white rounded-md" data-testid="inventory-reports-subtab">
                  <BarChart3 className="w-4 h-4" />
                  Inventory Reports
                </TabsTrigger>
                <TabsTrigger value="statements" className="flex items-center gap-2 data-[state=active]:bg-white rounded-md" data-testid="statements-subtab">
                  <Receipt className="w-4 h-4" />
                  Statements
                </TabsTrigger>
                <TabsTrigger value="transaction-history" className="flex items-center gap-2 data-[state=active]:bg-white rounded-md" data-testid="transaction-history-subtab">
                  <FileText className="w-4 h-4" />
                  Transaction History
                </TabsTrigger>
              </TabsList>

              {/* Inventory Reports Sub-tab */}
              <TabsContent value="inventory-reports">
                <InventoryReports />
              </TabsContent>

              {/* Statements Sub-tab */}
              <TabsContent value="statements">
                <InventoryStatements />
              </TabsContent>

              {/* Transaction History Sub-tab (Original Reports) */}
              <TabsContent value="transaction-history">
                <div>
                  <Card className="mb-6">
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-base">Filters</CardTitle>
                        <Button
                          onClick={() => {
                            if (transactions.length === 0) {
                              toast.error("No transactions to download");
                              return;
                            }
                            const dateStr = new Date().toISOString().split('T')[0];
                            const excelData = transactions.map((trans) => ({
                              "Date & Time": new Date(trans.transaction_date).toLocaleString(),
                              "Product": trans.product_name,
                              "Vendor": trans.vendor_name,
                              "Type": trans.transaction_type.toUpperCase(),
                              "Quantity (kg)": trans.quantity,
                              "Notes": trans.notes || "-"
                            }));
                            const ws = XLSX.utils.json_to_sheet(excelData);
                            const wb = XLSX.utils.book_new();
                            XLSX.utils.book_append_sheet(wb, ws, "Transactions");
                            XLSX.writeFile(wb, `transactions_${dateStr}.xlsx`);
                            toast.success("Report downloaded successfully");
                          }}
                          variant="outline"
                          className="border border-stone-300 bg-white hover:bg-stone-50"
                          data-testid="download-report-btn"
                        >
                          <Download className="w-4 h-4 mr-2" />Download Report
                        </Button>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
                        <div className="space-y-2">
                          <Label>Product</Label>
                          <Select value={reportFilters.product_id || "all"} onValueChange={(value) => setReportFilters({ ...reportFilters, product_id: value === "all" ? "" : value })}>
                            <SelectTrigger>
                              <SelectValue placeholder="All products" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="all">All products</SelectItem>
                              {products.map((p) => (
                                <SelectItem key={p.id} value={p.id}>{p.product_name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label>Vendor</Label>
                          <Select value={reportFilters.vendor_id || "all"} onValueChange={(value) => setReportFilters({ ...reportFilters, vendor_id: value === "all" ? "" : value })}>
                            <SelectTrigger>
                              <SelectValue placeholder="All vendors" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="all">All vendors</SelectItem>
                              {vendors.map((v) => (
                                <SelectItem key={v.id} value={v.id}>{v.vendor_name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label>Start Date</Label>
                          <Input
                            type="date"
                            value={reportFilters.start_date}
                            onChange={(e) => setReportFilters({ ...reportFilters, start_date: e.target.value })}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>End Date</Label>
                          <Input
                            type="date"
                            value={reportFilters.end_date}
                            onChange={(e) => setReportFilters({ ...reportFilters, end_date: e.target.value })}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Sort By</Label>
                          <Select value={reportFilters.sort_by} onValueChange={(value) => setReportFilters({ ...reportFilters, sort_by: value })}>
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="date">Date</SelectItem>
                              <SelectItem value="product">Product</SelectItem>
                              <SelectItem value="vendor">Vendor</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <Button
                        onClick={() => setReportFilters({
                          product_id: "",
                          vendor_id: "",
                          start_date: "",
                          end_date: "",
                          sort_by: "date"
                        })}
                        variant="outline"
                        className="mt-4"
                      >
                        Clear Filters
                      </Button>
                    </CardContent>
                  </Card>

                  {/* Transaction History Table */}
                  <Card>
                    <CardHeader>
                      <CardTitle>Transaction History</CardTitle>
                      <CardDescription>{transactions.length} transactions found</CardDescription>
                    </CardHeader>
                    <CardContent className="p-0">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Date & Time</TableHead>
                            <TableHead>Product</TableHead>
                            <TableHead>Vendor</TableHead>
                            <TableHead>Category</TableHead>
                            <TableHead>Type</TableHead>
                            <TableHead className="text-left">Quantity (kg)</TableHead>
                            <TableHead>Notes</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {transactions.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={7} className="text-center py-8 text-stone-500">
                                No transactions found for selected filters.
                              </TableCell>
                            </TableRow>
                          ) : (
                            transactions.map((trans) => {
                              const product = products.find(p => p.id === trans.product_id);
                              return (
                                <TableRow key={trans.id}>
                                  <TableCell className="font-medium">
                                    {new Date(trans.transaction_date).toLocaleString()}
                                  </TableCell>
                                  <TableCell>{trans.product_name}</TableCell>
                                  <TableCell>{trans.vendor_name}</TableCell>
                                  <TableCell>
                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800 border border-amber-200">
                                      {product?.category || "N/A"}
                                    </span>
                                  </TableCell>
                                  <TableCell>
                                    {trans.transaction_type === 'in' ? (
                                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-lime-100 text-lime-800 border border-lime-200">
                                        <ArrowUpCircle className="w-3 h-3 mr-1" />In
                                      </span>
                                    ) : (
                                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-rose-100 text-rose-800 border border-rose-200">
                                        <ArrowDownCircle className="w-3 h-3 mr-1" />Out
                                      </span>
                                    )}
                                  </TableCell>
                                  <TableCell className="text-left font-medium">{trans.quantity.toFixed(2)}</TableCell>
                                  <TableCell className="text-stone-500">{trans.notes || "-"}</TableCell>
                                </TableRow>
                              );
                            })
                          )}
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>
            </Tabs>
          </TabsContent>

          {/* Manufacturing Tab */}
          <TabsContent value="manufacturing" className="space-y-6">
            <div className="flex justify-between items-center">
              <div>
                <h3 className="text-lg font-outfit font-semibold text-stone-900">{t('manufacturing.title')}</h3>
                <p className="text-sm text-stone-500">{t('manufacturing.subtitle')}</p>
              </div>
              {hasPermission('manager') && (
                <Sheet open={isManufacturingSheetOpen} onOpenChange={setIsManufacturingSheetOpen}>
                  <SheetTrigger asChild>
                    <Button
                      data-testid="create-batch-btn"
                      onClick={() => {
                        setManufacturingForm({
                          inputs: [],
                          output_product_id: "",
                          output_quantity: 0,
                          process_loss: 0,
                          process_loss_notes: ""
                        });
                        setInputItems([{ product_id: "", quantity: 0 }]);
                        setEditingBatch(null);  // Clear editing state for new batch
                      }}
                      className="bg-lime-700 text-white hover:bg-lime-800"
                    >
                      <Plus className="w-4 h-4 mr-2" />{t('manufacturing.createBatch')}
                    </Button>
                  </SheetTrigger>
                  <SheetContent className="overflow-y-auto w-[500px] sm:max-w-lg">
                    <SheetHeader>
                      <SheetTitle>{editingBatch ? 'Edit Manufacturing Batch' : t('manufacturing.createBatch')}</SheetTitle>
                      <SheetDescription>
                        {editingBatch ? `Editing batch: ${editingBatch.batch_number}` : t('manufacturing.subtitle')}
                      </SheetDescription>
                    </SheetHeader>
                    <form onSubmit={handleManufacturingSubmit} className="space-y-6 mt-6">
                      {/* Input Materials */}
                      <div className="space-y-3">
                        <div className="flex justify-between items-center">
                          <Label className="text-base font-semibold">{t('manufacturing.inputMaterials')}</Label>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={addInputItem}
                            data-testid="add-input-item-btn"
                          >
                            <Plus className="w-3 h-3 mr-1" /> Add Input
                          </Button>
                        </div>
                        {inputItems.map((item, index) => (
                          <div key={index} className="flex gap-2 items-end p-3 bg-stone-50 rounded-lg">
                            <div className="flex-1">
                              <Label className="text-xs">Product</Label>
                              <Select 
                                value={item.product_id} 
                                onValueChange={(value) => updateInputItem(index, 'product_id', value)}
                              >
                                <SelectTrigger className="mt-1" data-testid={`input-product-${index}`}>
                                  <SelectValue placeholder="Select product" />
                                </SelectTrigger>
                                <SelectContent>
                                  {products.map((p) => (
                                    <SelectItem key={p.id} value={p.id}>{p.product_name}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="w-28">
                              <Label className="text-xs">{t('transactions.quantity')} ({t('common.kg')})</Label>
                              <Input
                                type="number"
                                step="0.01"
                                min="0.01"
                                value={item.quantity}
                                onChange={(e) => updateInputItem(index, 'quantity', e.target.value)}
                                className="mt-1"
                                data-testid={`input-qty-${index}`}
                              />
                            </div>
                            {inputItems.length > 1 && (
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => removeInputItem(index)}
                                className="text-rose-600 hover:text-rose-700 mb-0.5"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        ))}
                        <div className="text-sm text-stone-600 p-2 bg-blue-50 rounded">
                          {t('manufacturing.totalInputQty')}: <span className="font-semibold">{inputItems.reduce((sum, item) => sum + (item.quantity || 0), 0).toFixed(2)} {t('common.kg')}</span>
                        </div>
                      </div>
                      
                      {/* Output Product */}
                      <div className="border-t border-stone-200 pt-4">
                        <Label className="text-base font-semibold">{t('manufacturing.outputProduct')}</Label>
                        <div className="mt-3 space-y-3">
                          <div className="space-y-2">
                            <Label>{t('manufacturing.selectOutput')}</Label>
                            <Select 
                              value={manufacturingForm.output_product_id} 
                              onValueChange={(value) => setManufacturingForm({ ...manufacturingForm, output_product_id: value })}
                            >
                              <SelectTrigger data-testid="output-product-select">
                                <SelectValue placeholder="Select finished product" />
                              </SelectTrigger>
                              <SelectContent>
                                {products.map((p) => (
                                  <SelectItem key={p.id} value={p.id}>{p.product_name}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-2">
                            <Label>{t('manufacturing.outputQuantity')} ({t('common.kg')})</Label>
                            <Input
                              type="number"
                              step="0.01"
                              min="0.01"
                              value={manufacturingForm.output_quantity}
                              onChange={(e) => setManufacturingForm({ ...manufacturingForm, output_quantity: parseFloat(e.target.value) || 0 })}
                              data-testid="output-quantity-input"
                            />
                          </div>
                        </div>
                      </div>
                      
                      {/* Process Loss */}
                      <div className="border-t border-stone-200 pt-4">
                        <Label className="text-base font-semibold text-amber-700">{t('manufacturing.processLoss')}</Label>
                        <div className="mt-3 space-y-3">
                          <div className="space-y-2">
                            <Label>{t('manufacturing.processLoss')} ({t('common.kg')})</Label>
                            <Input
                              type="number"
                              step="0.01"
                              min="0"
                              value={manufacturingForm.process_loss}
                              onChange={(e) => setManufacturingForm({ ...manufacturingForm, process_loss: parseFloat(e.target.value) || 0 })}
                              data-testid="process-loss-input"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>{t('manufacturing.processLossNotes')}</Label>
                            <Input
                              value={manufacturingForm.process_loss_notes}
                              onChange={(e) => setManufacturingForm({ ...manufacturingForm, process_loss_notes: e.target.value })}
                              placeholder="e.g., Moisture loss, spillage"
                              data-testid="process-loss-notes-input"
                            />
                          </div>
                        </div>
                        <div className="text-sm text-stone-600 p-2 bg-amber-50 rounded mt-3">
                          {t('manufacturing.netOutput')}: <span className="font-semibold">
                            {(manufacturingForm.output_quantity - manufacturingForm.process_loss).toFixed(2)} {t('common.kg')}
                          </span>
                        </div>
                      </div>
                      
                      <SheetFooter>
                        <Button type="submit" className="bg-lime-700 text-white hover:bg-lime-800" data-testid="submit-batch-btn">
                          {editingBatch ? 'Update Batch' : t('manufacturing.createBatch')}
                        </Button>
                      </SheetFooter>
                    </form>
                  </SheetContent>
                </Sheet>
              )}
            </div>

            {/* Manufacturing Batches Table */}
            <Card>
              <CardHeader>
                <CardTitle>{t('manufacturing.title')} History</CardTitle>
                <CardDescription>{manufacturingBatches.length} batches recorded</CardDescription>
              </CardHeader>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('manufacturing.batchNumber')}</TableHead>
                      <TableHead>{t('transactions.date')}</TableHead>
                      <TableHead>{t('manufacturing.inputMaterials')}</TableHead>
                      <TableHead>{t('manufacturing.outputProduct')}</TableHead>
                      <TableHead className="text-left">{t('manufacturing.outputQuantity')}</TableHead>
                      <TableHead className="text-left">{t('manufacturing.processLoss')}</TableHead>
                      <TableHead className="text-left">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {manufacturingBatches.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center py-8 text-stone-500">
                          {t('common.noData')}. {t('manufacturing.createBatch')} to get started.
                        </TableCell>
                      </TableRow>
                    ) : (
                      manufacturingBatches.map((batch) => (
                        <TableRow key={batch.id} data-testid={`batch-row-${batch.id}`}>
                          <TableCell className="font-medium">
                            <span className="inline-flex items-center px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs font-mono">
                              {batch.batch_number}
                            </span>
                          </TableCell>
                          <TableCell>
                            <div className="text-sm">
                              <div>{new Date(batch.batch_date).toLocaleDateString('en-IN')}</div>
                              <div className="text-xs text-stone-400">
                                {new Date(batch.batch_date).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="space-y-1">
                              {batch.inputs?.map((input, idx) => (
                                <div key={idx} className="text-sm">
                                  <span className="text-stone-600">{input.product_name}</span>
                                  <span className="text-stone-400 ml-2">({input.quantity} kg)</span>
                                </div>
                              ))}
                            </div>
                          </TableCell>
                          <TableCell>
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-lime-100 text-lime-800 border border-lime-200">
                              {batch.output_product_name}
                            </span>
                          </TableCell>
                          <TableCell className="text-left font-medium text-lime-700">
                            +{batch.output_quantity.toFixed(2)} {t('common.kg')}
                          </TableCell>
                          <TableCell className="text-left">
                            {batch.process_loss > 0 ? (
                              <span className="text-amber-600" title={batch.process_loss_notes || ''}>
                                -{batch.process_loss.toFixed(2)} {t('common.kg')}
                              </span>
                            ) : (
                              <span className="text-stone-400">-</span>
                            )}
                          </TableCell>
                          <TableCell className="text-left">
                            <div className="flex gap-1 justify-end">
                              {hasPermission('manager') && (
                                <Button
                                  onClick={() => {
                                    // Set editing batch to track this is an UPDATE, not CREATE
                                    setEditingBatch(batch);
                                    setManufacturingForm({
                                      inputs: batch.inputs || [],
                                      output_product_id: batch.output_product_id,
                                      output_quantity: batch.output_quantity,
                                      process_loss: batch.process_loss || 0,
                                      process_loss_notes: batch.process_loss_notes || ""
                                    });
                                    setInputItems(batch.inputs?.map(inp => ({
                                      product_id: inp.product_id,
                                      quantity: inp.quantity
                                    })) || [{ product_id: "", quantity: 0 }]);
                                    setIsManufacturingSheetOpen(true);
                                  }}
                                  variant="ghost"
                                  size="sm"
                                  className="text-blue-600 hover:text-blue-700"
                                  data-testid={`edit-batch-${batch.id}`}
                                  title="Edit Batch"
                                >
                                  <Edit2 className="w-4 h-4" />
                                </Button>
                              )}
                              {currentUser?.role === 'admin' && (
                                <Button
                                  onClick={async () => {
                                    if (window.confirm(`Delete batch ${batch.batch_number}? This will reverse all inventory changes.`)) {
                                      try {
                                        await axios.delete(`${API}/manufacturing/batch/${batch.id}`, { withCredentials: true });
                                        toast.success(`Batch ${batch.batch_number} deleted. Inventory restored.`);
                                        fetchManufacturingBatches();
                                        fetchInventory();
                                      } catch (error) {
                                        toast.error(error.response?.data?.detail || "Failed to delete batch");
                                      }
                                    }
                                  }}
                                  variant="ghost"
                                  size="sm"
                                  className="text-rose-600 hover:text-rose-700"
                                  data-testid={`delete-batch-${batch.id}`}
                                  title="Delete Batch"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* User Management Tab (Admin Only) */}
          {currentUser?.role === 'admin' && (
            <TabsContent value="users">
              <UserManagement currentUser={currentUser} />
            </TabsContent>
          )}

          {/* Audit Logs Tab (Admin Only) */}
          {currentUser?.role === 'admin' && (
            <TabsContent value="audit">
              <AuditLogs />
            </TabsContent>
          )}

          {/* Backup Settings Tab (Admin Only) */}
          {currentUser?.role === 'admin' && (
            <TabsContent value="backup" className="space-y-6">
              {/* Local Backup & Restore */}
              <LocalBackupRestore />
              
              {/* Google Sheets Backup */}
              <div className="pt-6 border-t border-stone-200">
                <h3 className="text-lg font-semibold text-stone-900 mb-4 flex items-center gap-2">
                  <svg className="w-5 h-5" viewBox="0 0 24 24">
                    <path fill="#0F9D58" d="M19.5 11.5V19a2.5 2.5 0 01-2.5 2.5H7A2.5 2.5 0 014.5 19V5A2.5 2.5 0 017 2.5h5.5l7 9z"/>
                    <path fill="#F4B400" d="M12.5 2.5v7a2 2 0 002 2h7"/>
                  </svg>
                  Google Sheets Backup (Cloud)
                </h3>
                <BackupSettings />
              </div>
            </TabsContent>
          )}
        </Tabs>

        {/* Print Modal */}
        {showPrintModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-6xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-4 border-b border-stone-200 flex justify-between items-center">
                <h3 className="text-lg font-semibold">Print Stock Report</h3>
                <Button
                  onClick={() => setShowPrintModal(false)}
                  variant="ghost"
                  size="sm"
                >
                  Close
                </Button>
              </div>
              <div className="p-6">
                <PrintStock inventory={inventory} warehouse={currentWarehouse} />
              </div>
            </div>
          </div>
        )}

        {/* Challan Modal */}
        {showChallanModal && selectedChallanTransactions.length > 0 && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-4 border-b border-stone-200 flex justify-between items-center bg-amber-50">
                <h3 className="text-lg font-semibold flex items-center gap-2 text-amber-800">
                  <Truck className="w-5 h-5" />
                  Print Delivery Challan ({selectedChallanTransactions.length} item{selectedChallanTransactions.length > 1 ? 's' : ''})
                </h3>
                <Button
                  onClick={() => {
                    setShowChallanModal(false);
                    setSelectedChallanTransactions([]);
                    setSelectedTransactionIds([]);
                  }}
                  variant="ghost"
                  size="sm"
                >
                  Close
                </Button>
              </div>
              <div className="p-6">
                <PrintChallan 
                  transactions={selectedChallanTransactions}
                  products={products}
                  warehouse={currentWarehouse}
                  onClose={() => {
                    setShowChallanModal(false);
                    setSelectedChallanTransactions([]);
                    setSelectedTransactionIds([]);
                  }}
                />
              </div>
            </div>
          </div>
        )}

        {/* Saved Challans Modal */}
        {showSavedChallansModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-6xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <SavedChallans 
                  onClose={() => setShowSavedChallansModal(false)}
                  currentUser={currentUser}
                />
              </div>
            </div>
          </div>
        )}

        {/* Opening Stock Modal */}
        {showOpeningStockModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-md w-full">
              <div className="p-4 border-b border-stone-200 flex justify-between items-center bg-blue-50">
                <h3 className="text-lg font-semibold flex items-center gap-2 text-blue-800">
                  <Package className="w-5 h-5" />
                  Set Opening Stock
                </h3>
                <Button
                  onClick={() => {
                    setShowOpeningStockModal(false);
                    setOpeningStockForm({ product_id: '', quantity: 0 });
                  }}
                  variant="ghost"
                  size="sm"
                >
                  Close
                </Button>
              </div>
              <form onSubmit={handleOpeningStockSubmit} className="p-6 space-y-4">
                <p className="text-sm text-stone-600 mb-4">
                  Set opening stock for products in <strong>{currentWarehouse}</strong>
                </p>
                <div className="space-y-2">
                  <Label>Select Product *</Label>
                  <Select 
                    value={openingStockForm.product_id} 
                    onValueChange={(value) => setOpeningStockForm({...openingStockForm, product_id: value})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select product" />
                    </SelectTrigger>
                    <SelectContent>
                      {products.map((p) => (
                        <SelectItem key={p.id} value={p.id}>{p.product_name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Opening Stock Quantity (kg) *</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={openingStockForm.quantity}
                    onChange={(e) => setOpeningStockForm({...openingStockForm, quantity: parseFloat(e.target.value) || 0})}
                    placeholder="Enter opening stock in kg"
                  />
                </div>
                <div className="flex gap-3 pt-4">
                  <Button type="submit" className="bg-blue-600 text-white hover:bg-blue-700">
                    Save Opening Stock
                  </Button>
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={() => {
                      setShowOpeningStockModal(false);
                      setOpeningStockForm({ product_id: '', quantity: 0 });
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Transaction Edit Sheet */}
        <TransactionEditSheet
          transaction={editingTransaction}
          products={products}
          vendors={vendors}
          isOpen={showTransactionEditSheet}
          onClose={() => {
            setShowTransactionEditSheet(false);
            setEditingTransaction(null);
          }}
          onSuccess={(updatedTransaction) => {
            // Update the specific transaction in state (in-place update)
            setTransactions(prev => 
              prev.map(t => t.id === updatedTransaction.id ? updatedTransaction : t)
            );
            // Set the recently updated transaction for highlighting
            setRecentlyUpdatedTransactionId(updatedTransaction.id);
            // Clear the highlight after 4 seconds
            setTimeout(() => {
              setRecentlyUpdatedTransactionId(null);
            }, 4000);
            // Refresh inventory since transaction edit may affect stock
            fetchInventory();
          }}
        />

        {/* Transaction History Dialog */}
        <TransactionHistoryDialog
          transaction={selectedTransactionForHistory}
          isOpen={showTransactionHistory}
          onClose={() => {
            setShowTransactionHistory(false);
            setSelectedTransactionForHistory(null);
          }}
        />
      </div>
    </div>
  );
}

export default Dashboard;